import { useEffect } from "react";
import ReactTooltip from "react-tooltip";
import {
  buildFilteredReportText,
  flagLabel,
  flagTooltip,
} from "./filterTestHelpers";
import { FilterTestRow } from "./types";

const OUTCOME_TOOLTIP_ID = "cb-outcome";
const OUTCOME_TOOLTIP_DELAY_MS = 100;

interface FilterTestResultsProps {
  rows: FilterTestRow[];
  filteredReportTestId?: string;
}

function ResultBadge({ flag }: { flag: number }) {
  const isIncluded = flag === 1;
  return (
    <span
      data-tip={flagTooltip(flag)}
      data-for={OUTCOME_TOOLTIP_ID}
      className={
        "inline-flex rounded-full px-2 py-1 text-xs font-semibold " +
        (isIncluded
          ? "bg-yellow-200 text-yellow-800"
          : "bg-gray-200 text-gray-800")
      }
    >
      {flagLabel(flag)}
    </span>
  );
}

const FilterTestResults = ({
  rows,
  filteredReportTestId,
}: FilterTestResultsProps) => {
  useEffect(() => {
    if (rows.length === 0) return;
    ReactTooltip.rebuild();
  }, [rows]);

  if (rows.length === 0) return null;

  const includedCount = rows.filter((row) => row.flag === 1).length;
  const excludedCount = rows.length - includedCount;
  const filteredReport = buildFilteredReportText(rows);

  return (
    <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-800">Filter results</h3>
        <p className="text-xs text-gray-500">
          {includedCount} included · {excludedCount} excluded
        </p>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Included stays on the report. Excluded is removed from the report. Hover
        a badge for details.
      </p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[480px] text-left text-sm">
          <thead>
            <tr className="text-gray-500">
              <th className="pb-2 pr-4 font-medium">Sample post</th>
              <th className="w-40 pb-2 font-medium">Report outcome</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-gray-200 align-top">
                <td className="py-3 pr-4 text-gray-700">{row.text}</td>
                <td className="py-3">
                  <ResultBadge flag={row.flag} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ReactTooltip
        id={OUTCOME_TOOLTIP_ID}
        delayShow={OUTCOME_TOOLTIP_DELAY_MS}
        effect="solid"
        className="max-w-xs"
      />

      <label className="mt-5 block">
        <span className="text-sm font-medium text-gray-700">
          Report output (included posts)
        </span>
        <textarea
          readOnly
          className="mt-1 h-32 w-full rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-700"
          value={filteredReport}
          data-testid={filteredReportTestId}
        />
      </label>
    </div>
  );
};

export default FilterTestResults;
